import React, { useCallback, useState, useRef } from 'react';
import { HelpPanel, Box, SpaceBetween, Button, Link, Modal, Alert, Pagination, Popover } from '@amzn/awsui-components-core';
import CopyToClipboard from '@amzn/awsui-components-core/copy-to-clipboard';
import { Avatar, AvatarSize } from '@amzn/stencil-react-components/avatar';
import { DateTime } from 'luxon';
import { RecentMeeting, MeetingNotification, AttendeePriority } from '../interfaces/interface';
import { MEETING_CONSTANTS, PRIORITY_COLORS } from '../constants/EAHuddleConstants';
import './InfoPanel.scss';
import { EmployeePopoverCard } from './attendees/EmployeePopoverCard';
import { generateMultiBadgerUrl } from '../utils/attendeeUtils';
import { EAHuddleInformation } from './EAHuddleInformation';
import { NotificationBell } from './notifications/NotificationBell';

interface InfoPanelProps {
  favoriteLoginNames: Set<string>;
  existingLoginNames: Set<string>;
  onFavoriteClick: (loginName: string, priority?: AttendeePriority) => void;
  recentMeetings: RecentMeeting[];
  totalMeetingCount: number;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onDeleteMeeting: (meetingId: string) => void;
  isReadOnly?: boolean;
  isMeetingRegistered?: boolean;
  notificationActions: {
    unreadCount: number;
    allNotifications: MeetingNotification[];
    isLoading: boolean;
    loadNotifications: () => Promise<void>;
    markAsRead: (meetingId: string, notificationId: string) => Promise<void>;
    markAllAsRead: () => Promise<void>;
    dismissNotification: (meetingId: string, notificationId: string) => Promise<void>;
  };
  isRefreshing?: boolean;
  onRefresh?: () => void;
  refreshedAgo?: string;
}

export const InfoPanel: React.FC<InfoPanelProps> = ({
  favoriteLoginNames,
  existingLoginNames,
  onFavoriteClick,
  recentMeetings,
  totalMeetingCount,
  currentPage,
  totalPages,
  onPageChange,
  onDeleteMeeting,
  isReadOnly = false,
  isMeetingRegistered = false,
  notificationActions,
  isRefreshing = false,
  onRefresh,
  refreshedAgo
}) => {
  const [showAbout, setShowAbout] = useState(false);
  const favorites = Array.from(favoriteLoginNames);

  // Delete confirmation state
  const [pendingDeleteMeeting, setPendingDeleteMeeting] = useState<{ meetingId: string; subject: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Favorites pagination
  const [favoritesPage, setFavoritesPage] = useState(1);
  const favoritesPageSize = MEETING_CONSTANTS.FAVORITES_PAGE_SIZE;
  const favoritesTotalPages = Math.ceil(favorites.length / favoritesPageSize);
  const paginatedFavorites = favorites.slice((favoritesPage - 1) * favoritesPageSize, favoritesPage * favoritesPageSize);

  // Per-popover attendee pagination: meetingId → page index (1-based)
  const [attendeePages, setAttendeePages] = useState<Record<string, number>>({});

  // Ref guard to prevent double-click on add buttons (no visual state needed)
  const pendingAdds = useRef(new Set<string>());

  const handleDeleteConfirm = useCallback(async () => {
    if (!pendingDeleteMeeting) return;
    setIsDeleting(true);
    try {
      await onDeleteMeeting(pendingDeleteMeeting.meetingId);
    } finally {
      setIsDeleting(false);
      setPendingDeleteMeeting(null);
    }
  }, [pendingDeleteMeeting, onDeleteMeeting]);

  const handleFavoriteAdd = useCallback(
    (loginName: string, priority?: AttendeePriority) => {
      if (pendingAdds.current.has(loginName)) return;
      pendingAdds.current.add(loginName);
      onFavoriteClick(loginName, priority);
      // Release guard after existingLoginNames has time to update
      setTimeout(() => pendingAdds.current.delete(loginName), 5000);
    },
    [onFavoriteClick]
  );

  return (
    <>
      <HelpPanel>
        <SpaceBetween direction="vertical" size="xxs">
          {/* ── Badge + Refresh + Notification + About ────────────── */}
          <div className={`info-panel-section${showAbout ? '' : ' info-panel-section--border-bottom'}`}>
            <SpaceBetween direction="vertical" size="xs">
              <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                {onRefresh && refreshedAgo && <span className="last-refreshed-badge">Refreshed {refreshedAgo}</span>}
                {onRefresh && (
                  <Button variant="icon" iconName="refresh" loading={isRefreshing} onClick={onRefresh} ariaLabel="Refresh meeting data" />
                )}
                <NotificationBell
                  unreadCount={notificationActions.unreadCount}
                  notifications={notificationActions.allNotifications}
                  isLoading={notificationActions.isLoading}
                  onOpen={notificationActions.loadNotifications}
                  onMarkAsRead={notificationActions.markAsRead}
                  onDismiss={notificationActions.dismissNotification}
                  onMarkAllAsRead={notificationActions.markAllAsRead}
                />
              </SpaceBetween>
              <Button variant="inline-link" onClick={() => setShowAbout((prev) => !prev)}>
                {showAbout ? 'Go Back' : 'About Huddle+'}
              </Button>
              {showAbout && <EAHuddleInformation />}
            </SpaceBetween>
          </div>

          {!showAbout && (
            <>
              {/* ── Favorites Section ─────────────────────────────────── */}
              <div className="info-panel-section">
                <SpaceBetween direction="vertical" size="xs">
                  <Box fontSize="heading-s" fontWeight="bold">
                    ⭐ Favorites ({favorites.length})
                  </Box>

                  {favorites.length === 0 ? (
                    <Box textAlign="center" color="text-body-secondary" padding={{ vertical: 's' }}>
                      <Box variant="p" fontSize="body-s">
                        Click the star icon next to an attendee to add them here
                      </Box>
                    </Box>
                  ) : (
                    <SpaceBetween direction="vertical" size="xs">
                      {paginatedFavorites.map((loginName) => {
                        const isAlreadyAdded = existingLoginNames.has(loginName);
                        return (
                          <div key={loginName} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <EmployeePopoverCard loginName={loginName} renderWithPortal>
                              <Avatar username={loginName} size={AvatarSize.ExtraSmall} imageAltText={`Profile image of ${loginName}`} />
                            </EmployeePopoverCard>
                            {isAlreadyAdded ? (
                              <span className="priority-badge added-badge">Already Added</span>
                            ) : !isReadOnly && isMeetingRegistered ? (
                              <Button
                                variant="icon"
                                iconName="add-plus"
                                ariaLabel={`Add ${loginName} to attendees`}
                                onClick={() => handleFavoriteAdd(loginName)}
                              />
                            ) : null}
                          </div>
                        );
                      })}
                      {favoritesTotalPages > 1 && (
                        <Pagination
                          currentPageIndex={favoritesPage}
                          pagesCount={favoritesTotalPages}
                          onChange={({ detail }) => setFavoritesPage(detail.currentPageIndex)}
                        />
                      )}
                    </SpaceBetween>
                  )}
                </SpaceBetween>
              </div>

              {/* ── Recent Meetings Section ───────────────────────────── */}
              <div className="info-panel-section info-panel-section--border-top">
                <SpaceBetween direction="vertical" size="xs">
                  <Box fontSize="heading-s" fontWeight="bold">
                    📋 My Meetings ({totalMeetingCount})
                  </Box>

                  {recentMeetings.length === 0 ? (
                    <Box textAlign="center" color="text-body-secondary" padding={{ vertical: 's' }}>
                      <Box variant="p" fontSize="body-s">
                        Create a meeting to see your meetings here
                      </Box>
                    </Box>
                  ) : (
                    <SpaceBetween direction="vertical" size="s">
                      {recentMeetings.map((meeting) => (
                        <div
                          key={meeting.meetingId}
                          style={{
                            padding: '8px',
                            borderRadius: '8px',
                            border: '1px solid var(--color-border-divider-default)',
                            backgroundColor: 'var(--color-background-container-content)'
                          }}
                        >
                          <SpaceBetween direction="vertical" size="xxs">
                            <Box fontSize="body-m" fontWeight="bold">
                              {meeting.subject}
                            </Box>

                            <Box fontSize="body-s" color="text-body-secondary">
                              {meeting.attendees.length > 0 ? (
                                <Popover
                                  triggerType="custom"
                                  position="bottom"
                                  size="large"
                                  dismissButton={true}
                                  renderWithPortal
                                  content={
                                    <SpaceBetween direction="vertical" size="xs">
                                      <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                                        <Box fontWeight="bold" fontSize="body-s">
                                          Attendees ({meeting.attendees.length})
                                        </Box>
                                        <Link href={generateMultiBadgerUrl(meeting.attendees.map((a) => a.loginName))} external fontSize="body-s">
                                          Multi Badger
                                        </Link>
                                      </SpaceBetween>
                                      {(() => {
                                        const PAGE_SIZE = 10;
                                        const page = attendeePages[meeting.meetingId] || 1;
                                        const totalAttendeePages = Math.ceil(meeting.attendees.length / PAGE_SIZE);
                                        const paged = meeting.attendees.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
                                        return (
                                          <>
                                            {paged.map((attendee) => {
                                              const isAlreadyAdded = existingLoginNames.has(attendee.loginName);
                                              return (
                                                <div key={attendee.loginName} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                  <EmployeePopoverCard loginName={attendee.loginName} renderWithPortal>
                                                    <Avatar
                                                      username={attendee.loginName}
                                                      size={AvatarSize.ExtraSmall}
                                                      imageAltText={`Profile image of ${attendee.loginName}`}
                                                    />
                                                  </EmployeePopoverCard>
                                                  <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <span
                                                      className="priority-badge"
                                                      style={{
                                                        backgroundColor: PRIORITY_COLORS[attendee.priority]?.background,
                                                        color: PRIORITY_COLORS[attendee.priority]?.color
                                                      }}
                                                    >
                                                      {attendee.priority.charAt(0).toUpperCase() + attendee.priority.slice(1)}
                                                    </span>
                                                    {isAlreadyAdded ? (
                                                      <span className="priority-badge added-badge">Already Added</span>
                                                    ) : !isReadOnly && isMeetingRegistered ? (
                                                      <Button
                                                        variant="icon"
                                                        iconName="add-plus"
                                                        ariaLabel={`Add ${attendee.loginName} to attendees as ${attendee.priority}`}
                                                        onClick={() => handleFavoriteAdd(attendee.loginName, attendee.priority)}
                                                      />
                                                    ) : null}
                                                  </div>
                                                </div>
                                              );
                                            })}
                                            {totalAttendeePages > 1 && (
                                              <Pagination
                                                currentPageIndex={page}
                                                pagesCount={totalAttendeePages}
                                                onChange={({ detail }) =>
                                                  setAttendeePages((prev) => ({ ...prev, [meeting.meetingId]: detail.currentPageIndex }))
                                                }
                                              />
                                            )}
                                          </>
                                        );
                                      })()}
                                    </SpaceBetween>
                                  }
                                >
                                  <Link variant="info" fontSize="body-s">
                                    {meeting.attendees.length} attendee{meeting.attendees.length !== 1 ? 's' : ''}
                                  </Link>
                                </Popover>
                              ) : (
                                <Box display="inline" fontSize="body-s">
                                  0 attendees
                                </Box>
                              )}{' '}
                              · {DateTime.fromISO(meeting.createdAt).toRelative() ?? ''}
                            </Box>

                            <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                              <Link href={meeting.tinyUrl} external fontSize="body-s">
                                Open link
                              </Link>
                              <CopyToClipboard
                                copyButtonAriaLabel={`Copy link for ${meeting.subject}`}
                                copyErrorText="Failed to copy"
                                copySuccessText="Link copied"
                                textToCopy={meeting.tinyUrl}
                                variant="icon"
                              />
                              <Button
                                variant="icon"
                                iconName="remove"
                                ariaLabel={`Delete ${meeting.subject}`}
                                onClick={() => setPendingDeleteMeeting({ meetingId: meeting.meetingId, subject: meeting.subject })}
                              />
                            </SpaceBetween>
                          </SpaceBetween>
                        </div>
                      ))}

                      {totalPages > 1 && (
                        <Pagination
                          currentPageIndex={currentPage}
                          pagesCount={totalPages}
                          onChange={({ detail }) => onPageChange(detail.currentPageIndex)}
                        />
                      )}
                    </SpaceBetween>
                  )}
                </SpaceBetween>
              </div>
            </>
          )}
        </SpaceBetween>
      </HelpPanel>

      {/* Delete confirmation modal */}
      {pendingDeleteMeeting && (
        <Modal
          visible={true}
          onDismiss={() => setPendingDeleteMeeting(null)}
          header="Delete Meeting"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={() => setPendingDeleteMeeting(null)}>
                  Cancel
                </Button>
                <span className="blue-button">
                  <Button variant="primary" onClick={handleDeleteConfirm} loading={isDeleting} disabled={isDeleting}>
                    Delete
                  </Button>
                </span>
              </SpaceBetween>
            </Box>
          }
        >
          <SpaceBetween direction="vertical" size="s">
            <Alert type="warning" statusIconAriaLabel="Warning">
              This will remove the meeting from your My Meetings list.
            </Alert>
            <Box>
              Are you sure you want to delete <b>{pendingDeleteMeeting.subject}</b>? The meeting will be removed from your list, but the shared link
              will continue to work.
            </Box>
          </SpaceBetween>
        </Modal>
      )}
    </>
  );
};
